// ES module (see shared/i18n/index.js's own header comment for why —
// shared/ is consumed directly by Vite's dev/build pipeline, a
// `module.exports =` would throw in the browser).
//
// Strings genuinely used by BOTH admin/ and portal/ — app chrome, the
// language toggle, the two components in shared/components/ that both apps
// render (PasswordInput, and the generic-error/validation codes), and the
// three ApiError codes every fetch() call in shared/api/client.js can throw
// regardless of which app is calling it (NETWORK_ERROR, INTERNAL_ERROR) or
// which form is being filled in (VALIDATION_ERROR).
//
// This file split out of the old monolithic shared/i18n/strings.js — see
// PROGRESS.md's "Blocked / needs Ermiyas" entry on the portal bundle budget
// for why: that one file shipped every admin-only string (catalog, cost
// management, users, settings, the pricing workspace...) to portal too,
// because both apps imported it wholesale and nothing tree-shakes a single
// object literal's properties. admin-only and portal-only strings now live
// in shared/i18n/admin.js and shared/i18n/portal.js instead, composed with
// this file by each app's own src/i18n.js — see that file's header comment
// for how the composition and registration works.
//
// Every id below keeps its ORIGINAL value unchanged from strings.js — this
// is a pure move, not a rewrite.

const strings = {
  // --- App shell / nav ----------------------------------------------------
  'app.brand': { am: 'ስነ ኢትኤል', en: 'SineEthel' },
  'nav.logout': { am: 'ውጣ', en: 'Log out' },
  'lang.toggleToAmharic': { am: 'አማርኛ', en: 'አማርኛ' },
  'lang.toggleToEnglish': { am: 'English', en: 'English' },
  'lang.label': { am: 'ቋንቋ', en: 'Language' },

  // --- 404 (shared page copy — admin's NotFoundPage.jsx additionally shows
  // notFound.backLink, which stays admin-only since portal's own 404 uses
  // portal.login.backToLanding instead) --------------------------------
  'notFound.title': { am: 'ገጹ አልተገኘም', en: 'Page not found' },
  'notFound.body': {
    am: 'የፈለጉት ገጽ አልተገኘም ወይም ተንቀሳቅሷል።',
    en: 'The page you were looking for does not exist or has moved.',
  },

  // --- Password input (shared/components/PasswordInput, rendered by both
  // apps' own LoginPage) ---------------------------------------------------
  'passwordInput.show': { am: 'የይለፍ ቃል አሳይ', en: 'Show password' },
  'passwordInput.hide': { am: 'የይለፍ ቃል ደብቅ', en: 'Hide password' },

  // --- Feedback primitives (shared/components/Feedback) — chrome only; the
  // toast message and confirm title/body are passed in already translated
  // by the calling screen. Generic 'feedback.*' strings below are the
  // fallbacks a screen uses when it has nothing more specific to say.
  'feedback.toast.dismiss': { am: 'ዝጋ', en: 'Dismiss' },
  'feedback.confirm.ok': { am: 'አረጋግጥ', en: 'Confirm' },
  'feedback.confirm.cancel': { am: 'ተወው', en: 'Cancel' },
  'feedback.saved': { am: 'ተቀምጧል', en: 'Saved' },
  'feedback.actionFailed': {
    am: 'እርምጃው አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    en: "That didn't go through. Please try again.",
  },

  // --- Generic ApiError codes (shared/api/client.js) — every fetch() call
  // in either app can throw these regardless of which endpoint or form is
  // involved: VALIDATION_ERROR for client-side required-field checks (every
  // LoginPage/GuestXStep in both apps renders it directly), NETWORK_ERROR
  // when fetch() itself throws (offline, DNS failure), INTERNAL_ERROR as the
  // fallback for any server error code neither app has a specific mapping
  // for.
  'error.VALIDATION_ERROR': {
    am: 'የገባው መረጃ ትክክል አይደለም። እባክዎ ያረጋግጡ።',
    en: 'The submitted data is invalid. Please check and try again.',
  },
  'error.INTERNAL_ERROR': {
    am: 'ያልታወቀ ስህተት ተፈጥሯል። እባክዎ እንደገና ይሞክሩ።',
    en: 'An unexpected error occurred. Please try again.',
  },
  'error.NETWORK_ERROR': {
    am: 'ከአገልጋዩ ጋር መገናኘት አልተቻለም። የበይነመረብ ግንኙነትዎን ያረጋግጡ።',
    en: 'Could not reach the server. Check your connection.',
  },

  // Shown beneath the generic message in both apps' ErrorState whenever an
  // ApiError is available — the code the API returned (e.g. VALIDATION_ERROR,
  // SESSION_EXPIRED), so a user reporting a problem can name what failed
  // without opening devtools. The full response is logged to the console in
  // development (shared/api/client.js).
  'error.codeLabel': { am: 'የስህተት ኮድ', en: 'Error code' },

  // --- Proforma document (shared/components/ProformaDocument) --------------
  // The HTML rendering of a quotation's figures — reused by the customer's
  // in-page review (portal /proforma/:token) and the admin read-only
  // viewer, so the two can never drift. Lives in common.js because BOTH
  // apps render this component.
  'proformaDoc.title': { am: 'ፕሮፎርማ', en: 'Proforma' },
  'proformaDoc.notIssued': { am: 'ገና አልተሰጠም', en: 'Not yet issued' },
  'proformaDoc.notIssuedNote': {
    am: 'ይህ ፕሮፎርማ ገና አልጸደቀም — የፕሮፎርማ ቁጥር አልተመደበም እና ለገዥ አልተላከም። ይህ አሁን ያለው ረቂቅ ነው።',
    en: 'This proforma has not been approved — no number is allocated and it has not been sent to the buyer. This is the current draft.',
  },
  'proformaDoc.downloadPdf': { am: 'PDF አውርድ', en: 'Download PDF' },
  'proformaDoc.pdfPending': { am: 'PDF ገና አልተዘጋጀም', en: 'PDF not yet rendered' },
  'proformaDoc.organization': { am: 'ድርጅት', en: 'Organization' },
  'proformaDoc.request': { am: 'የጥያቄ ቁጥር', en: 'Request' },
  'proformaDoc.issuedOn': { am: 'የተሰጠበት ቀን', en: 'Issued on' },
  'proformaDoc.validUntil': { am: 'የሚያበቃበት ቀን', en: 'Valid until' },
  // Editable-terms task: OUR delivery commitment (quotations.delivery_days)
  // and payment terms (quotations.payment_terms) — distinct from the
  // buyer's own requested needed-by date, which lives on the request/
  // workspace screens, not this document.
  'proformaDoc.deliveryLeadTime.label': { am: 'የማድረሻ ጊዜ', en: 'Delivery' },
  'proformaDoc.deliveryLeadTime.value': {
    am: 'ትዕዛዝና ክፍያ ከተረጋገጠ በኋላ በ{days} ቀን(ት) ውስጥ',
    en: '{days} day(s) from confirmed order and payment',
  },
  'proformaDoc.paymentTerms': { am: 'የክፍያ ውሎች', en: 'Payment terms' },
  'proformaDoc.col.line': { am: 'ተራ', en: 'Line' },
  'proformaDoc.col.description': { am: 'መግለጫ', en: 'Description' },
  'proformaDoc.col.qty': { am: 'ብዛት', en: 'Qty' },
  'proformaDoc.col.unitPrice': { am: 'የአንዱ ዋጋ', en: 'Unit price' },
  'proformaDoc.col.lineTotal': { am: 'ድምር', en: 'Line total' },
  'proformaDoc.subtotal': { am: 'ንዑስ ድምር', en: 'Subtotal' },
  'proformaDoc.vat': { am: 'ተ.እ.ታ', en: 'VAT' },
  'proformaDoc.total': { am: 'ጠቅላላ ድምር', en: 'Total' },

  // --- Sealed-bid tenders (migration 020) — the buyer-facing skeleton
  // state: no lines, no totals, just this explanation. Deliberately calm,
  // not error-toned (root CLAUDE.md: a zero-line or gated state is never a
  // failure state) — this is expected behavior for a sealed tender, not
  // something broken.
  'proformaDoc.sealed.title': {
    am: 'የታሸገ ፕሮፎርማ',
    en: 'Sealed proforma',
  },
  'proformaDoc.sealed.bodyScheduled': {
    am: 'ይህ ፕሮፎርማ የታሸገ ነው። ዋጋዎቹ እና ዝርዝሩ በጨረታ መክፈቻ ሰዓት፣ {openingAt}፣ ይገለጣሉ።',
    en: 'This proforma is sealed. Its prices and item list will be revealed at the scheduled bid opening, {openingAt}.',
  },
  'proformaDoc.sealed.bodyUnscheduled': {
    am: 'ይህ ፕሮፎርማ የታሸገ ነው። ቡድናችን ጨረታውን ሲከፍት ዋጋዎቹ እና ዝርዝሩ ይገለጣሉ። የተወሰነ ቀን ገና አልተያዘም።',
    en: 'This proforma is sealed. Our team will reveal its prices and item list once the bid is opened. No specific time has been scheduled yet.',
  },
  'proformaDoc.sealed.wasSealedNote': {
    am: 'ይህ ፕሮፎርማ እስከ ጨረታ መክፈቻ ድረስ የታሸገ ነበር።',
    en: 'This proforma was sealed until the bid opened.',
  },
};

export default strings;
